import { useEffect, useState } from "react";
import { MessageSquare } from "lucide-react";
import { useToast } from "@/components/ui";
import { ConversationView, MessagesSkeleton } from "./ConversationView";
import { api, ApiError } from "@/lib/api";
import type { Contact, Conversation } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * One thread per contact, period — every channel (SMS, email, IG,
 * WhatsApp, internal note) interleaves chronologically. The composer
 * lives at the bottom with a channel picker that decides what
 * provider gets the next outbound; the rest of the thread doesn't
 * care.
 *
 * Used in two places:
 *   - the contact-detail page (bordered card)
 *   - the standalone /inbox right pane (borderless, parent owns chrome)
 *
 * `initialConversationId` is honored from the inbox sidebar's row
 * click but quickly resolves to the contact's single conversation —
 * we keep the prop for backward compat with existing callers.
 */
export function ContactInboxPanel({
  contact,
  initialConversationId,
  bordered = true,
  onAfterSend,
}: {
  contact: Contact;
  initialConversationId?: string | null;
  bordered?: boolean;
  /** Notifies the host so it can refresh sidebar conversation lists,
   *  unread counts, etc. Fires after each successful send + on the
   *  internal poll cycle. */
  onAfterSend?: () => void;
}) {
  const toast = useToast();
  // Trust the parent's hint when provided. The /inbox sidebar always
  // knows the conversation the user just clicked, so we should NEVER
  // need to refetch the conversation list here — that was the source
  // of the "switching chats spins twice" bug (fetch resolved AFTER the
  // panel had already rendered the previous contact's conversation).
  // Falls back to a contact-scoped lookup only on the contact-detail
  // page, where the parent doesn't know the conversation id.
  const [conversation, setConversation] = useState<Conversation | null>(
    initialConversationId ? ({ id: initialConversationId } as Conversation) : null
  );
  // Distinguishes "still looking up the contact's conversation" from
  // "lookup completed; this contact genuinely has no conversation yet."
  // Without this we'd flash ConversationView's "Start a conversation —
  // first message goes via …" empty state during the fallback fetch on
  // the contact-detail page, before swapping back to the skeleton and
  // then real messages.
  const [resolvingConversation, setResolvingConversation] = useState(
    !initialConversationId
  );

  // Hard reset on every contact OR initialConversationId change so we
  // never render a stale conversation under a new contact.
  useEffect(() => {
    setConversation(
      initialConversationId ? ({ id: initialConversationId } as Conversation) : null
    );
    setResolvingConversation(!initialConversationId);
  }, [contact.id, initialConversationId]);

  // Fallback lookup ONLY when the parent didn't provide an id (contact
  // detail page). The inbox path skips this entirely.
  useEffect(() => {
    if (initialConversationId) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<{ conversations: Conversation[] }>(
          "/conversations",
          { status: "all" }
        );
        if (cancelled) return;
        const mine = d.conversations.find((c) => c.contact_id === contact.id);
        // Identity gate: only adopt this conversation if it still
        // belongs to the currently-mounted contact. Protects against
        // racing fetches when the contact switches mid-flight.
        if (mine && mine.contact_id !== contact.id) return;
        setConversation(mine ?? null);
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "Failed");
      } finally {
        if (!cancelled) setResolvingConversation(false);
      }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [contact.id, initialConversationId]);

  return (
    <div className={cn(
      "flex flex-col h-full min-h-96",
      // overflow-hidden is required when bordered — without it the
      // composer's border-t and the conversation list's edges paint
      // over the rounded corners.
      bordered && "rounded-xl border border-zinc-800/70 bg-zinc-900/30 overflow-hidden",
    )}>
      {/* While we're still resolving whether this contact has a
          conversation, show the same skeleton ConversationView would
          show for messages. Stops the contact-detail page from briefly
          flashing the "Start a conversation — first message goes via
          Email." empty state before the real thread loads. */}
      {resolvingConversation ? (
        <MessagesSkeleton />
      ) : !conversation && !contact.email && !contact.phone && !Object.values(contact.socials ?? {}).some(Boolean) ? (
        // No reachable identity AND no existing thread = nothing to show.
        // (If a thread exists from a past channel that's since been
        // cleared, we still render it so the user can read history.)
        <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-800/60">
            <MessageSquare className="h-5 w-5 text-zinc-500" />
          </div>
          <p className="mt-3 text-sm text-zinc-300">Nowhere to send a message</p>
          <p className="mt-1 text-xs text-zinc-500 max-w-xs">
            Add an email, phone, or social profile on the left and a chat will appear here automatically.
          </p>
        </div>
      ) : (
        <ConversationView
          contact={contact}
          conversationId={conversation?.id ?? null}
          onCreated={(id) => setConversation((prev) => prev?.id === id ? prev : { ...(prev ?? ({} as Conversation)), id } as Conversation)}
          onAfterSend={() => { onAfterSend?.(); }}
        />
      )}
    </div>
  );
}
